import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, ChevronRight, LogOut, ArrowLeft, MapPin, Save, RefreshCw, Filter, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Doctor', 'Medical', 'Lab'];
const STATUSES = ['Prospect', 'Follow-up', 'Converted', 'Lost'];

const STATUS_STYLE = {
  'Prospect': { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  'Follow-up': { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  'Converted': { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  'Lost': { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
};

const emptyLead = () => ({
  date_first_contact: new Date().toISOString().split('T')[0],
  category: '',
  facility_name: '',
  contact_person: '',
  phone: '',
  location: '',
  target_lat: '',
  target_lng: '',
  initial_feedback: '',
  status: 'Prospect',
  followup1_date: '',
  followup1_feedback: '',
  followup2_date: '',
  followup2_feedback: '',
  conversion_date: '',
  remarks: '',
  visit_logs: [],
});

const canAccessB2B = (account) => account?.role === 'b2b' || account?.role === 'admin' || account?.module_permissions?.b2b;

const getGpsStamp = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('Geolocation is not supported on this device'));
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (position) => {
      const gpsLat = Number(position.coords.latitude.toFixed(6));
      const gpsLng = Number(position.coords.longitude.toFixed(6));
      resolve({
        gps_lat: gpsLat,
        gps_lng: gpsLng,
        gps_map_link: `https://www.google.com/maps?q=${gpsLat},${gpsLng}`,
      });
    },
    () => reject(new Error('Location permission is required for B2B visit capture')),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
  );
});

/* ─── Login Screen ─── */
const B2BLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/b2b/login`, { email: username, password }, { withCredentials: true });
      onLogin(data);
    } catch {
      toast.error('Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A2540] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>B2B</span>
          </div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Partner Tracker</h1>
          <p className="text-white/60 text-sm mt-1">Aaric Medical Center</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 space-y-4">
          <div>
            <Label className="text-white/80 text-sm">Username</Label>
            <Input value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" className="bg-white/10 border-white/20 text-white placeholder:text-white/40" required data-testid="b2b-username" />
          </div>
          <div>
            <Label className="text-white/80 text-sm">Password</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" className="bg-white/10 border-white/20 text-white placeholder:text-white/40" required data-testid="b2b-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-xl py-3 font-semibold" data-testid="b2b-login-submit">
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

/* ─── Lead Card ─── */
const LeadCard = ({ lead, onClick }) => {
  const s = STATUS_STYLE[lead.status] || STATUS_STYLE['Prospect'];
  return (
    <button onClick={onClick} className="w-full text-left bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-all active:scale-[0.98] border border-[#E2E8F0]" data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <span className="text-xs font-mono text-[#94A3B8]">{lead.lead_id}</span>
          <h3 className="text-base font-bold text-[#0A2540] leading-tight">{lead.facility_name || 'Untitled'}</h3>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {lead.status}
          </span>
          <ChevronRight className="h-4 w-4 text-[#94A3B8]" />
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-[#475569]">
        <span className="bg-[#F1F5F9] px-2 py-0.5 rounded capitalize">{lead.category}</span>
        {lead.contact_person && <span>{lead.contact_person}</span>}
        {lead.location && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{lead.location}</span>}
      </div>
      <p className="text-xs text-[#94A3B8] mt-1">{lead.date_first_contact}</p>
    </button>
  );
};

/* ─── Lead Form ─── */
const LeadForm = ({ lead, isEdit, onSave, onCancel }) => {
  const [form, setForm] = useState(lead || emptyLead());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(lead || emptyLead());
  }, [lead]);

  const showFollowup = ['Follow-up', 'Converted'].includes(form.status);
  const showConversion = form.status === 'Converted';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pb-6">
      {/* Lead ID */}
      {isEdit && lead?.lead_id && (
        <div className="bg-[#F1F5F9] rounded-xl px-4 py-2 text-center">
          <span className="text-sm font-mono font-bold text-[#0A2540]">{lead.lead_id}</span>
        </div>
      )}

      <div>
        <Label className="text-sm font-semibold text-[#0A2540]">Date of First Contact</Label>
        <Input type="date" value={form.date_first_contact} onChange={e => update('date_first_contact', e.target.value)} required className="mt-1 h-12 rounded-xl" data-testid="b2b-date" />
      </div>

      <div>
        <Label className="text-sm font-semibold text-[#0A2540]">Category</Label>
        <select value={form.category} onChange={e => update('category', e.target.value)} required className="w-full mt-1 h-12 border rounded-xl px-3 text-sm bg-white" data-testid="b2b-category">
          <option value="">Select category...</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div>
        <Label className="text-sm font-semibold text-[#0A2540]">Name of Doctor/Facility</Label>
        <Input value={form.facility_name} onChange={e => update('facility_name', e.target.value)} required placeholder="e.g. Dr. Ramesh Clinic" className="mt-1 h-12 rounded-xl" data-testid="b2b-facility" />
      </div>

      <div>
        <Label className="text-sm font-semibold text-[#0A2540]">Contact Person</Label>
        <Input value={form.contact_person} onChange={e => update('contact_person', e.target.value)} placeholder="Name of contact" className="mt-1 h-12 rounded-xl" data-testid="b2b-contact" />
      </div>

      <div>
        <Label className="text-sm font-semibold text-[#0A2540]">Phone Number</Label>
        <Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="10-digit number" className="mt-1 h-12 rounded-xl" data-testid="b2b-phone" />
      </div>

      <div>
        <Label className="text-sm font-semibold text-[#0A2540]">Location / Area</Label>
        <Input value={form.location} onChange={e => update('location', e.target.value)} placeholder="e.g. T. Nagar, Chennai" className="mt-1 h-12 rounded-xl" data-testid="b2b-location" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-sm font-semibold text-[#0A2540]">Expected Latitude</Label>
          <Input type="number" step="0.000001" value={form.target_lat} onChange={e => update('target_lat', e.target.value)} placeholder="e.g. 13.082680" className="mt-1 h-12 rounded-xl" data-testid="b2b-target-lat" required />
        </div>
        <div>
          <Label className="text-sm font-semibold text-[#0A2540]">Expected Longitude</Label>
          <Input type="number" step="0.000001" value={form.target_lng} onChange={e => update('target_lng', e.target.value)} placeholder="e.g. 80.270721" className="mt-1 h-12 rounded-xl" data-testid="b2b-target-lng" required />
        </div>
      </div>

      <div>
        <Label className="text-sm font-semibold text-[#0A2540]">Day 1 Initial Feedback</Label>
        <textarea value={form.initial_feedback} onChange={e => update('initial_feedback', e.target.value)} rows={3} placeholder="Notes from first visit..." className="w-full mt-1 border rounded-xl px-3 py-2.5 text-sm resize-none" data-testid="b2b-feedback" />
      </div>

      <div>
        <Label className="text-sm font-semibold text-[#0A2540]">Current Status</Label>
        <div className="grid grid-cols-4 gap-2 mt-1">
          {STATUSES.map(st => {
            const s = STATUS_STYLE[st];
            return (
              <button type="button" key={st} onClick={() => update('status', st)}
                className={`py-2.5 rounded-xl text-xs font-bold transition-all ${form.status === st ? `${s.bg} ${s.text} ring-2 ring-offset-1 ring-slate-300` : 'bg-[#F1F5F9] text-[#475569]'}`}
                data-testid={`b2b-status-${st.toLowerCase()}`}
              >
                {st}
              </button>
            );
          })}
        </div>
      </div>

      {/* Progressive Disclosure */}
      {showFollowup && (
        <div className="space-y-4 bg-amber-50/50 rounded-2xl p-4 border border-amber-200/50">
          <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Follow-up Details</p>
          <div>
            <Label className="text-sm text-[#0A2540]">Follow-up 1 Date</Label>
            <Input type="date" value={form.followup1_date} onChange={e => update('followup1_date', e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="b2b-fu1-date" />
          </div>
          <div>
            <Label className="text-sm text-[#0A2540]">Follow-up 1 Feedback</Label>
            <textarea value={form.followup1_feedback} onChange={e => update('followup1_feedback', e.target.value)} rows={2} placeholder="Follow-up notes..." className="w-full mt-1 border rounded-xl px-3 py-2 text-sm resize-none" data-testid="b2b-fu1-feedback" />
          </div>
          <div>
            <Label className="text-sm text-[#0A2540]">Follow-up 2 Date</Label>
            <Input type="date" value={form.followup2_date} onChange={e => update('followup2_date', e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="b2b-fu2-date" />
          </div>
          <div>
            <Label className="text-sm text-[#0A2540]">Follow-up 2 Feedback</Label>
            <textarea value={form.followup2_feedback} onChange={e => update('followup2_feedback', e.target.value)} rows={2} placeholder="Follow-up notes..." className="w-full mt-1 border rounded-xl px-3 py-2 text-sm resize-none" data-testid="b2b-fu2-feedback" />
          </div>
        </div>
      )}

      {showConversion && (
        <div className="space-y-4 bg-green-50/50 rounded-2xl p-4 border border-green-200/50">
          <p className="text-xs font-bold text-green-700 uppercase tracking-wider">Conversion Details</p>
          <div>
            <Label className="text-sm text-[#0A2540]">Conversion Date</Label>
            <Input type="date" value={form.conversion_date} onChange={e => update('conversion_date', e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="b2b-conv-date" />
          </div>
        </div>
      )}

      {(showFollowup || showConversion) && (
        <div>
          <Label className="text-sm font-semibold text-[#0A2540]">Remarks / Next Action</Label>
          <textarea value={form.remarks} onChange={e => update('remarks', e.target.value)} rows={2} placeholder="Next steps..." className="w-full mt-1 border rounded-xl px-3 py-2.5 text-sm resize-none" data-testid="b2b-remarks" />
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button type="button" onClick={onCancel} variant="outline" className="flex-1 h-12 rounded-xl text-sm font-semibold">Cancel</Button>
        <Button type="submit" disabled={saving} className="flex-1 h-12 rounded-xl text-sm font-semibold bg-[#0A2540]" data-testid="b2b-save">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : (isEdit ? 'Update Lead' : 'Save Lead')}
        </Button>
      </div>
    </form>
  );
};

const B2BVisitSection = ({ lead, onUpdated }) => {
  const [capturing, setCapturing] = useState('');
  const activeVisit = (lead.visit_logs || []).find((visit) => !visit.check_out);

  const captureVisit = async (endpoint) => {
    setCapturing(endpoint);
    try {
      const gpsStamp = await getGpsStamp();
      const { data } = await axios.post(`${API}/b2b/leads/${lead.id}/visit-${endpoint}`, gpsStamp, { withCredentials: true });
      toast.success(endpoint === 'checkin' ? 'B2B visit checked in with GPS' : 'B2B visit checked out with GPS');
      onUpdated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Visit update failed');
    }
    setCapturing('');
  };

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="b2b-visit-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-[#0A2540]">B2B Visit GPS</h3>
          <p className="mt-1 text-xs text-slate-500">Expected customer location must be set on this lead. Distance mismatch is flagged but not blocked.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" onClick={() => captureVisit('checkin')} className="rounded-xl bg-[#0A2540]" disabled={Boolean(activeVisit) || capturing === 'checkin'} data-testid="b2b-visit-checkin">
            {capturing === 'checkin' ? 'Capturing GPS...' : 'Visit Check In'}
          </Button>
          <Button type="button" onClick={() => captureVisit('checkout')} variant="outline" className="rounded-xl" disabled={!activeVisit || capturing === 'checkout'} data-testid="b2b-visit-checkout">
            {capturing === 'checkout' ? 'Capturing GPS...' : 'Visit Check Out'}
          </Button>
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-white p-3 text-sm text-slate-600">
        <p className="font-semibold text-[#0A2540]">Expected customer coordinates</p>
        <p className="mt-1">Lat: {lead.target_lat || '-'} | Lng: {lead.target_lng || '-'}</p>
        {lead.target_map_link && <a href={lead.target_map_link} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-sky-700 hover:underline">Open expected location</a>}
      </div>

      <div className="mt-4 space-y-3">
        {(lead.visit_logs || []).length === 0 ? (
          <p className="text-sm text-slate-500">No B2B visit sessions captured yet.</p>
        ) : (lead.visit_logs || []).slice().reverse().map((visit) => (
          <div key={visit.id} className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-600" data-testid={`b2b-visit-log-${visit.id}`}>
            <p className="font-semibold text-[#0A2540]">Check-in distance: {visit.check_in?.distance_meters ?? '-'} m</p>
            <a href={visit.check_in?.map_link} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-sky-700 hover:underline">Open check-in map</a>
            {visit.check_out ? (
              <>
                <p className="mt-3 font-semibold text-[#0A2540]">Check-out distance: {visit.check_out.distance_meters} m</p>
                <a href={visit.check_out.map_link} target="_blank" rel="noreferrer" className="mt-1 inline-flex text-emerald-700 hover:underline">Open check-out map</a>
              </>
            ) : <p className="mt-3 text-amber-700">Visit currently open.</p>}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Main B2B Page ─── */
const B2BTracker = ({ embeddedUser = null, embeddedMode = false }) => {
  const [user, setUser] = useState(embeddedUser);
  const [checking, setChecking] = useState(!embeddedUser);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');
  const [editLead, setEditLead] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    if (embeddedUser) {
      setUser(embeddedUser);
      setChecking(false);
      return;
    }

    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        if (canAccessB2B(res.data)) setUser(res.data);
        setChecking(false);
      })
      .catch(() => {
        setUser(null);
        setChecking(false);
      });
  }, [embeddedUser]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/b2b/leads`, { withCredentials: true }),
        axios.get(`${API}/b2b/stats`, { withCredentials: true }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Failed to load leads'); }
    setLoading(false);
  }, []);

  useEffect(() => { if (user) fetchLeads(); }, [user, fetchLeads]);

  const handleSaveNew = async (form) => {
    try {
      await axios.post(`${API}/b2b/leads`, form, { withCredentials: true });
      toast.success('Lead created');
      setView('list');
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleUpdate = async (form) => {
    try {
      const { data } = await axios.put(`${API}/b2b/leads/${editLead.id}`, form, { withCredentials: true });
      toast.success('Lead updated');
      if (data?.message) {
        const refreshed = await axios.get(`${API}/b2b/leads/${editLead.id}`, { withCredentials: true });
        setEditLead(refreshed.data);
      }
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleLogout = async () => {
    if (embeddedMode) return;
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  if (checking) return <div className={`${embeddedMode ? 'rounded-[1.75rem] bg-[#0A2540]' : 'min-h-screen bg-[#0A2540]'} flex items-center justify-center p-10`} data-testid="b2b-loading"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white" /></div>;
  if (!user) return <B2BLogin onLogin={setUser} />;

  const filtered = leads.filter(l => {
    const matchSearch = !search || l.facility_name?.toLowerCase().includes(search.toLowerCase()) || l.contact_person?.toLowerCase().includes(search.toLowerCase()) || l.lead_id?.toLowerCase().includes(search.toLowerCase()) || l.location?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'All' || l.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className={embeddedMode ? 'overflow-hidden rounded-[1.75rem] bg-[#F1F5F9] shadow-sm' : 'min-h-screen bg-[#F1F5F9]'} data-testid="b2b-tracker-shell">
      {/* Header */}
      <header className={`bg-[#0A2540] text-white px-4 py-3 ${embeddedMode ? '' : 'sticky top-0 z-30 safe-area-top'}`} data-testid="b2b-header">
        <div className={`${embeddedMode ? 'max-w-6xl' : 'max-w-lg'} mx-auto flex items-center justify-between gap-3`}>
          {view !== 'list' ? (
            <button onClick={() => { setView('list'); setEditLead(null); }} className="flex items-center gap-1 text-white/80 hover:text-white" data-testid="b2b-back-button">
              <ArrowLeft className="h-5 w-5" /> Back
            </button>
          ) : (
            <div className="flex items-center gap-3">
              {embeddedMode ? <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10"><Building2 className="h-6 w-6 text-sky-300" /></div> : null}
              <div>
                <h1 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{embeddedMode ? 'B2B Partner Tracker' : 'B2B Tracker'}</h1>
                <p className="text-xs text-white/50">AMC Partner Outreach</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            {view === 'list' && (
              <button onClick={() => { setView('new'); setEditLead(null); }} className="bg-[#0EA5E9] text-white rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-1" data-testid="b2b-new-lead">
                <Plus className="h-4 w-4" /> New Lead
              </button>
            )}
            {!embeddedMode && <button onClick={handleLogout} className="text-white/60 hover:text-white p-2" data-testid="b2b-logout"><LogOut className="h-5 w-5" /></button>}
          </div>
        </div>
      </header>

      <div className={`${embeddedMode ? 'max-w-6xl' : 'max-w-lg'} mx-auto px-4 py-4`}>
        {view === 'list' && (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-4 gap-2 mb-4" data-testid="b2b-stats">
                {[
                  { label: 'Total', value: stats.total, color: 'bg-[#0A2540] text-white' },
                  { label: 'Prospect', value: stats.prospect, color: 'bg-blue-50 text-blue-700' },
                  { label: 'Follow-up', value: stats.followup, color: 'bg-amber-50 text-amber-700' },
                  { label: 'Converted', value: stats.converted, color: 'bg-green-50 text-green-700' },
                ].map(s => (
                  <div key={s.label} className={`${s.color} rounded-xl p-3 text-center`} data-testid={`b2b-stat-${s.label.toLowerCase()}`}>
                    <p className="text-xl font-bold">{s.value}</p>
                    <p className="text-[10px] font-medium opacity-75">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Search & Filter */}
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                <Input placeholder="Search leads..." className="pl-10 h-11 rounded-xl bg-white" value={search} onChange={e => setSearch(e.target.value)} data-testid="b2b-search" />
              </div>
              <button onClick={() => setShowFilter(!showFilter)} className={`px-3 rounded-xl border ${showFilter ? 'bg-[#0A2540] text-white border-[#0A2540]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`} data-testid="b2b-filter-toggle">
                <Filter className="h-4 w-4" />
              </button>
              <button onClick={fetchLeads} className="px-3 rounded-xl border bg-white text-[#475569] border-[#E2E8F0]" data-testid="b2b-refresh">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>

            {showFilter && (
              <div className="flex gap-2 mb-4 flex-wrap">
                {['All', ...STATUSES].map(st => (
                  <button key={st} onClick={() => setFilterStatus(st)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filterStatus === st ? 'bg-[#0A2540] text-white' : 'bg-white text-[#475569]'}`}
                    data-testid={`b2b-filter-${st.toLowerCase()}`}
                  >
                    {st}
                  </button>
                ))}
              </div>
            )}

            {/* Lead List */}
            {loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2540]" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-[#94A3B8] text-sm mb-4">No leads found</p>
                <button onClick={() => setView('new')} className="text-[#0EA5E9] font-semibold text-sm">+ Add your first lead</button>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map(lead => (
                  <LeadCard key={lead.id} lead={lead} onClick={() => { setEditLead(lead); setView('edit'); }} />
                ))}
              </div>
            )}
          </>
        )}

        {view === 'new' && (
          <>
            <h2 className="text-xl font-bold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>New Lead</h2>
            <LeadForm onSave={handleSaveNew} onCancel={() => setView('list')} />
          </>
        )}

        {view === 'edit' && editLead && (
          <>
            <h2 className="text-xl font-bold text-[#0A2540] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Lead</h2>
            <p className="text-sm text-[#94A3B8] mb-4">{editLead.lead_id} - {editLead.facility_name}</p>
            <LeadForm lead={editLead} isEdit onSave={handleUpdate} onCancel={() => { setView('list'); setEditLead(null); }} />
            <B2BVisitSection lead={editLead} onUpdated={setEditLead} />
          </>
        )}
      </div>
    </div>
  );
};

export default B2BTracker;
